import WaveSystem from './waves.js';
import NoteSystem from './notes.js';
import ParticleSystem from './particles.js';
import Input from './input.js';
import Renderer from './renderer.js';
import CosmosSystem, { stateFromSnapshot } from './cosmos.js';
import { pointAtPhase, pointAtTime, orbitShapeAtTime, setOrbitVariant } from './orbit.js';
import { JUDGMENTS, COPY } from '../data/copy.js';
import { getChart } from '../data/songs.js';
import { hashString } from '../utils/math.js';
import { trackPlay } from '../utils/hub-play.js';

const SCORE_TAP = { perfect: 300, good: 120 };
const SCORE_HOLD = { perfect: 600, good: 350 };
const SCORE_SYNC = 220;
const OVERDRIVE_BEATS = 8;
const FEVER_COMBO = 20;
const TAP_HUE = 230;
const HOLD_HUE = 79;
const GOLD_HUE = 46;
const DOUBLE_HUE = 315;
const SCRUB_HUE = 190;

export default class Game {
  constructor({ canvas, audio, hud, onEnd }) {
    this.canvas = canvas;
    this.audio = audio;
    this.hud = hud;
    this.onEnd = onEnd;

    this.renderer = new Renderer(canvas);
    this.input = new Input(canvas);
    this.waves = new WaveSystem();
    this.notes = new NoteSystem();
    this.particles = new ParticleSystem();
    this.cosmos = new CosmosSystem();

    this.song = null;
    this.mode = 'play';
    this.difficulty = 'normal';
    this.running = false;
    this.score = 0;
    this.combo = 0;
    this.maxCombo = 0;
    this.energy = 0;
    this.overdrive = false;
    this.overdriveUntil = 0;
    this.overdriveSeconds = 0;
    this.fever = false;
    this.counts = { perfect: 0, good: 0, miss: 0, merge: 0, holds: 0 };

    this._raf = 0;
    this._lastTs = 0;
    this._ended = false;
    this._shake = 0;
    this._flash = 0;
    this._punch = 0;
    this._comboFxAt = 0;
    this._calibrationOffset = 0;

    this._onResize = () => this.renderer.resize();
    this._onDebugKey = (event) => {
      if (!import.meta.env.DEV || !['BracketLeft', 'BracketRight'].includes(event.code)) return;
      this._calibrationOffset += event.code === 'BracketRight' ? 0.01 : -0.01;
      this.hud?.setHint(`OFFSET ${this._calibrationOffset >= 0 ? '+' : ''}${this._calibrationOffset.toFixed(3)}s`);
    };
  }

  async start(song, mode = 'play', difficulty = 'normal') {
    // Chart analysis is fetched at runtime (daily songs land without rebuild).
    const chart = await getChart(song, difficulty);
    this.stopLoop(false);
    this.song = song;
    this.mode = mode;
    this.difficulty = difficulty;
    trackPlay({ gameSlug: 'orbeat', gameKind: 'original' });
    this.score = 0;
    this.combo = 0;
    this.maxCombo = 0;
    this.energy = 0;
    this.overdrive = false;
    this.overdriveUntil = 0;
    this.overdriveSeconds = 0;
    this.fever = false;
    this.counts = { perfect: 0, good: 0, miss: 0, merge: 0, holds: 0 };
    this._ended = false;
    this._shake = 0;
    this._flash = 0;
    this._punch = 0;
    this._comboFxAt = 0;

    // Fresh trajectory each play; the daily shares one orbit for everyone.
    setOrbitVariant(mode === 'daily'
      ? hashString(`orbit-${song.id}`)
      : hashString(`orbit-${song.id}-${Date.now()}`));

    this.notes.load(chart, this.difficulty);
    this.waves.reset();
    this.particles.reset();
    this.cosmos.begin(hashString(`${song.id}-${Date.now()}`));
    this.renderer.resize();
    this.input.bind();
    window.addEventListener('resize', this._onResize);
    window.addEventListener('keydown', this._onDebugKey);

    this.hud?.reset();
    this.hud?.show();
    this.hud?.setHint(COPY.hudHint);
    this.hud?.setAudioState(true, 'starting');

    // Native media play stays in the original user click call stack.
    this.audio.playPreparedSong(song);
    this.running = true;
    this._lastTs = performance.now();
    this._raf = requestAnimationFrame((time) => this._frame(time));
  }

  quit() {
    this.stopLoop(true);
  }

  stopLoop(triggerEnd) {
    this.running = false;
    cancelAnimationFrame(this._raf);
    this.input.unbind();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('keydown', this._onDebugKey);
    this.audio.stop();
    this.hud?.hide();
    if (triggerEnd && !this._ended && this.song) {
      this._ended = true;
      this.onEnd?.(this._buildResult(false));
    }
  }

  _buildResult(completed) {
    const judged = this.counts.perfect + this.counts.good + this.counts.miss;
    const accuracy = judged ? (this.counts.perfect + this.counts.good * 0.6) / judged : 0;
    let grade = 'C';
    if (accuracy >= 0.95 && this.counts.miss <= 2) grade = 'S';
    else if (accuracy >= 0.85) grade = 'A';
    else if (accuracy >= 0.7) grade = 'B';

    return {
      score: Math.floor(this.score),
      maxCombo: this.maxCombo,
      grade,
      perfect: this.counts.perfect,
      good: this.counts.good,
      miss: this.counts.miss,
      merge: this.counts.merge,
      holds: this.counts.holds,
      overdriveSeconds: this.overdriveSeconds,
      songId: this.song?.id ?? 'unknown',
      songTitle: this.song?.title ?? 'Unknown',
      mode: this.mode,
      difficulty: this.difficulty,
      completed,
      // A universe is only born from a song played all the way through.
      universe: completed ? this.cosmos.snapshot() : null
    };
  }

  _frame(timestamp) {
    if (!this.running) return;
    const dt = Math.min(0.05, (timestamp - this._lastTs) / 1000);
    this._lastTs = timestamp;
    const songTime = this.audio.currentTime + (this.song.offset || 0) + this._calibrationOffset;

    this._handleInput(songTime);
    this._handleMisses(songTime);
    this._updateHold(dt);
    this._updateOverdrive(songTime, dt);

    const { merges } = this.waves.update(dt);
    for (const merge of merges) {
      this.counts.merge++;
      this.score += SCORE_SYNC * this._multiplier();
      if (!this.overdrive) this.energy = Math.min(100, this.energy + 8);
      this.particles.burst(merge.x, merge.y, { count: 8, hue: merge.hue, speed: 0.38, life: 0.55 });
      this.cosmos.igniteCore();
      this.hud?.flashJudgment(JUDGMENTS.merge.label, JUDGMENTS.merge.kind);
      this._flash = 0.18;
    }

    this.particles.update(dt);
    this._shake = Math.max(0, this._shake - dt * 3);
    this._flash = Math.max(0, this._flash - dt * 3);
    this._punch = Math.max(0, this._punch - dt * 12.5);

    const progress = Math.min(1, Math.max(0, this.audio.currentTime / this.song.duration));
    this.cosmos.update(dt, this.maxCombo, progress);
    const judgedHits = this.counts.perfect + this.counts.good;
    if (judgedHits >= 6) this.hud?.clearHint();

    const overdriveDisplay = this.overdrive
      ? Math.max(0, ((this.overdriveUntil - songTime) / ((60 / this.song.bpm) * OVERDRIVE_BEATS)) * 100)
      : this.energy;

    this.hud?.update({
      score: this.score,
      combo: this.combo,
      progress,
      energy: overdriveDisplay,
      overdrive: this.overdrive,
      fever: this.fever,
      era: this.cosmos.era
    });

    const direction = this.song.direction || 1;
    const playhead = pointAtTime(songTime, this.song.bpm, direction);
    const visibleNotes = this.notes.getVisible(songTime);

    this.renderer.draw({
      mode: 'game',
      time: songTime,
      bpm: this.song.bpm,
      direction,
      beatPulse: this.audio.getBeatPulse(),
      combo: this.combo,
      comboTier: this.combo >= 50 ? 3 : this.combo >= 25 ? 2 : this.combo >= 10 ? 1 : 0,
      overdrive: this.overdrive,
      fever: this.fever,
      energy: overdriveDisplay,
      playhead,
      visibleNotes,
      activeHold: this.notes.getActiveHold(),
      shake: this._shake,
      flash: this._flash,
      punch: this._punch,
      waves: this.waves.getActive(),
      particles: this.particles.getActive(),
      cosmos: this.cosmos.getState(),
      showHints: judgedHits < 6
    });

    if (this.audio.hasEnded || songTime >= this.song.duration + 0.5) {
      this._finish();
      return;
    }
    this._raf = requestAnimationFrame((time) => this._frame(time));
  }

  _handleInput(songTime) {
    for (const event of this.input.consumeEvents()) {
      if (event.type === 'press') {
        const result = this.notes.tryPress(songTime);
        if (!result) continue;
        this._dispatchResult(result, songTime);
      } else {
        const result = this.notes.tryRelease(songTime);
        if (!result) continue;
        this._dispatchResult(result, songTime);
      }
    }

    // Scrub fuel: pointer wiggling / key mashing feeds the active zigzag.
    const agitation = this.input.consumeAgitation();
    if (agitation > 0) {
      const applied = this.notes.addAgitation(agitation);
      if (applied > 0) {
        this.score += applied * 15 * this._multiplier();
        const playhead = pointAtTime(songTime, this.song.bpm, this.song.direction || 1);
        this.particles.burst(playhead.x, playhead.y, {
          count: Math.min(4, Math.ceil(applied)),
          hue: SCRUB_HUE,
          speed: 0.32,
          life: 0.35
        });
      }
    }
  }

  _dispatchResult(result, songTime) {
    switch (result.kind) {
      case 'tap':
        this._registerCompletedHit(result, songTime, false);
        break;
      case 'doubleHalf':
        // First tap of a double: teaser feedback, score lands on completion.
        this.score += 50 * this._multiplier();
        this._spawnFeedback(result.note, songTime, DOUBLE_HUE, 4);
        break;
      case 'doubleComplete':
        this._registerCompletedHit(result, songTime, false, { double: true });
        break;
      case 'doubleSingle':
        // Second tap never came: resolves as a plain good single.
        this._registerCompletedHit(result, songTime, false);
        break;
      case 'holdStart':
        this.score += 50 * this._multiplier();
        this.hud?.flashJudgment(JUDGMENTS.hold.label, JUDGMENTS.hold.kind);
        this._spawnFeedback(result.note, songTime, HOLD_HUE, 4);
        break;
      case 'holdComplete':
        this._registerCompletedHit(result, songTime, true);
        break;
      case 'scrubStart':
        this.score += 50 * this._multiplier();
        this.hud?.flashJudgment(JUDGMENTS.scrub.label, JUDGMENTS.scrub.kind);
        this._spawnFeedback(result.note, songTime, SCRUB_HUE, 5);
        break;
      case 'scrubComplete':
        this._registerCompletedHit(result, songTime, true, { scrub: true });
        break;
      default: // holdFailed, scrubFailed
        this._registerMiss(result.note, songTime);
    }
  }

  _registerCompletedHit(result, songTime, isHold, { double = false, scrub = false } = {}) {
    const { judgment, note } = result;
    this.counts[judgment]++;
    if (isHold) this.counts.holds++;
    this.combo += double ? 2 : 1;
    this.maxCombo = Math.max(this.maxCombo, this.combo);

    // FEVER: 20+ combo without a miss, ×1.5 for as long as the combo holds.
    if (!this.fever && this.combo >= FEVER_COMBO) {
      this.fever = true;
      this.hud?.flashJudgment(JUDGMENTS.fever.label, JUDGMENTS.fever.kind);
    }

    const isGold = !isHold && note.gold && judgment === 'perfect';
    const scoreTable = isHold ? SCORE_HOLD : SCORE_TAP;
    const bonus = isGold ? 4 : double ? 1.5 : 1;
    this.score += scoreTable[judgment] * bonus * this._multiplier();
    if (!this.overdrive) {
      // +30% energy gains keep the Overdrive coming more often.
      const gain = judgment === 'perfect' ? (isHold ? 23 : 16) : (isHold ? 16 : 9);
      this.energy = Math.min(100, this.energy + gain + (isGold ? 20 : 0) + (double ? 8 : 0));
    }

    const feedback = isGold ? JUDGMENTS.gold : double ? JUDGMENTS.double : scrub ? JUDGMENTS.scrub : JUDGMENTS[judgment];
    this.hud?.flashJudgment(feedback.label, feedback.kind);
    this._flash = isGold ? 0.3 : judgment === 'perfect' ? 0.18 : 0.08;
    this._shake = isGold || double ? 0.3 : judgment === 'perfect' ? 0.1 : 0;
    if (judgment === 'perfect') this._punch = isGold ? 1.6 : double ? 1.3 : 1;
    const hue = isGold ? GOLD_HUE : double ? DOUBLE_HUE : scrub ? SCRUB_HUE : isHold ? HOLD_HUE : TAP_HUE;
    this._spawnFeedback(note, songTime, hue, judgment === 'perfect' ? 9 : 5);

    // Genesis: taps ignite layout stars, completed holds embrase a giant nebula.
    if (isHold) this.cosmos.igniteNebula(judgment);
    else this.cosmos.addStar(judgment);
    if (double) this.cosmos.addStar(judgment); // second tap ignites too
    if (scrub) this.cosmos.spawnComet();
    if (isGold) {
      const point = pointAtTime(note.t, this.song.bpm, this.song.direction || 1);
      this.cosmos.goldNova(point.x, point.y);
    }
    if (this.overdrive || double) this.cosmos.miniBurst();
    if (this.combo > 0 && this.combo % 15 === 0) this.cosmos.spawnComet();
    if (this.combo > 0 && this.combo % 25 === 0) this.cosmos.burst();

    if (this.combo > 0 && this.combo % 10 === 0 && this.combo !== this._comboFxAt) {
      this._comboFxAt = this.combo;
      this._flash = 0.28;
      const shape = orbitShapeAtTime(songTime, this.song.bpm);
      for (let i = 0; i < 4; i++) {
        const point = pointAtPhase(i / 4, shape, this.song.direction || 1);
        this.particles.burst(point.x, point.y, { count: 4, hue: i % 2 ? HOLD_HUE : TAP_HUE, speed: 0.28, life: 0.45 });
      }
    }
  }

  _spawnFeedback(note, songTime, hue, count) {
    const point = pointAtTime(note.t, this.song.bpm, this.song.direction || 1);
    this.waves.spawn({
      x: point.x,
      y: point.y,
      quality: note.judgment || note.pressJudgment || 'good',
      combo: this.combo,
      hue
    });
    this.particles.burst(point.x, point.y, { count, hue, speed: 0.24, life: 0.4 });
  }

  _handleMisses(songTime) {
    const { missed, failedHold, resolved } = this.notes.processMisses(songTime);
    for (const note of missed) this._registerMiss(note, songTime);
    if (failedHold) this._registerMiss(failedHold, songTime);
    for (const result of resolved) this._dispatchResult(result, songTime);
  }

  _registerMiss(note, songTime) {
    this.counts.miss++;
    this.combo = 0;
    this.fever = false;
    if (!this.overdrive) this.energy = Math.max(0, this.energy - 24);
    this.hud?.flashJudgment(JUDGMENTS.miss.label, JUDGMENTS.miss.kind);
    const point = pointAtTime(note.t || songTime, this.song.bpm, this.song.direction || 1);
    this.particles.burst(point.x, point.y, { count: 4, hue: 0, speed: 0.12, life: 0.3 });
    this.cosmos.addBlackHole();
  }

  _updateHold(dt) {
    if (!this.notes.getActiveHold()) return;
    this.score += dt * 70 * this._multiplier();
  }

  _updateOverdrive(songTime, dt) {
    if (this.overdrive) {
      this.overdriveSeconds += dt;
      if (songTime >= this.overdriveUntil) {
        this.overdrive = false;
        this.energy = 0;
        this.cosmos.setOverdrive(false);
      }
      return;
    }
    if (this.energy >= 100) {
      this.overdrive = true;
      this.overdriveUntil = songTime + (60 / this.song.bpm) * OVERDRIVE_BEATS;
      this.energy = 100;
      this._flash = 0.5;
      this.cosmos.supernova();
      this.cosmos.setOverdrive(true);
      this.hud?.flashJudgment(JUDGMENTS.overdrive.label, JUDGMENTS.overdrive.kind);
    }
  }

  _multiplier() {
    const comboMultiplier = 1 + Math.floor(this.combo / 10) * 0.25;
    return comboMultiplier * (this.overdrive ? 2 : 1) * (this.fever ? 1.5 : 1);
  }

  _finish() {
    this._ended = true;
    this.running = false;
    cancelAnimationFrame(this._raf);
    this.input.unbind();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('keydown', this._onDebugKey);
    this.audio.stop();
    this.hud?.hide();
    this.onEnd?.(this._buildResult(true));
  }

  startMenuBg() {
    this.stopLoop(false);
    this.renderer.resize();
    window.addEventListener('resize', this._onResize);
    const loop = (timestamp) => {
      if (this.running) return;
      const time = timestamp / 1000;
      const beat = 0.5;
      const phase = (time % beat) / beat;
      this.renderer.draw({
        mode: 'menu',
        time,
        bpm: 120,
        direction: 1,
        beatPulse: Math.max(0, 1 - phase * 3.2),
        combo: 0,
        comboTier: 0,
        overdrive: false,
        energy: 0,
        playhead: pointAtTime(time, 120, 1),
        visibleNotes: [],
        activeHold: null,
        shake: 0,
        flash: 0,
        waves: [],
        particles: [],
        showHints: false
      });
      this._raf = requestAnimationFrame(loop);
    };
    this._raf = requestAnimationFrame(loop);
  }

  stopMenuBg() {
    cancelAnimationFrame(this._raf);
    window.removeEventListener('resize', this._onResize);
  }

  /** Slowly drifting display of a stored universe snapshot (results, gallery). */
  startUniverseBg(snapshot) {
    this.stopLoop(false);
    this.renderer.resize();
    window.addEventListener('resize', this._onResize);
    const state = stateFromSnapshot(snapshot);
    const loop = (timestamp) => {
      if (this.running) return;
      const time = timestamp / 1000;
      if (state) state.rotation = time * 0.02;
      this.renderer.draw({
        mode: 'universe',
        time,
        beatPulse: 0,
        combo: 0,
        shake: 0,
        flash: 0,
        cosmos: state
      });
      this._raf = requestAnimationFrame(loop);
    };
    this._raf = requestAnimationFrame(loop);
  }
}
